// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Channel} from '@app/features/channel/models/Channel';
import type {FlatEmoji} from '@app/features/emoji/types/EmojiTypes';
import {ExpressionPickerPopout} from '@app/features/expressions/components/popouts/ExpressionPickerPopout';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Popout} from '@app/features/ui/popover/PopoverPopout';
import {canSendEmote, sendEmote} from '@app/features/voice_scene/commands/RoomSignalCommands';
import styles from '@app/features/voice_scene/components/VoiceSceneEmoteBar.module.css';
import type {RoomEmote} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import {ROOM_EMOTE_BAR} from '@fluxer/constants/src/RoomCharacters';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {PlusIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';

const REACTIONS_DESCRIPTOR = msg({
	message: 'Room reactions',
	comment:
		'Voice call 3D room: accessible name of the row of emoji buttons; a picked emoji pops up over your character for everyone in the room.',
});
const MORE_REACTIONS_DESCRIPTOR = msg({
	message: 'More reactions',
	comment:
		"Voice call 3D room: tooltip and accessible label of the + button after the reaction emojis; opens the emoji picker (standard emojis and this community's emojis).",
});

// A standard emoji, or an emoji of this community; the others in the picker are not sent (others in the room drop them).
function pickedEmote(emoji: FlatEmoji, guildId: string | undefined): RoomEmote | null {
	if (emoji.id) return emoji.guildId != null && emoji.guildId === guildId ? {kind: 'custom', id: emoji.id} : null;
	return emoji.surrogates ? {kind: 'emoji', emoji: emoji.surrogates} : null;
}

export const VoiceSceneEmoteBar = observer(function VoiceSceneEmoteBar({channel}: {channel: Channel}) {
	const {i18n} = useLingui();
	// Observable: false outside the voice room and while my emote limit holds. aria-disabled, not disabled: a keyboard
	// user keeps their place on the button, which a browser would take away. sendEmote holds the limit.
	const limited = !canSendEmote();
	const moreLabel = i18n._(MORE_REACTIONS_DESCRIPTOR);
	const pick = (emoji: FlatEmoji) => {
		const emote = pickedEmote(emoji, channel.guildId);
		if (emote) sendEmote(emote);
	};
	return (
		<div
			className={styles.bar}
			role="group"
			aria-label={i18n._(REACTIONS_DESCRIPTOR)}
			onMouseDown={(event) => event.preventDefault()}
			data-flx="voice-scene.voice-scene-emote-bar.bar"
		>
			{ROOM_EMOTE_BAR.map((emoji) => (
				<FocusRing key={emoji} offset={-2} data-flx="voice-scene.voice-scene-emote-bar.emoji-focus-ring">
					<button
						type="button"
						className={styles.emoji}
						aria-disabled={limited}
						onClick={() => sendEmote({kind: 'emoji', emoji})}
						data-flx="voice-scene.voice-scene-emote-bar.emoji-button.send"
					>
						{emoji}
					</button>
				</FocusRing>
			))}
			<Popout
				position="top"
				animationType="none"
				tooltip={moreLabel}
				shouldOpenOnClick={canSendEmote}
				render={({onClose}) => (
					<ExpressionPickerPopout
						channelId={channel.id}
						onEmojiSelect={pick}
						onClose={onClose}
						visibleTabs={['emojis']}
						data-flx="voice-scene.voice-scene-emote-bar.expression-picker-popout"
					/>
				)}
				data-flx="voice-scene.voice-scene-emote-bar.popout"
			>
				<FocusRing offset={-2} data-flx="voice-scene.voice-scene-emote-bar.more-focus-ring">
					<button
						type="button"
						className={styles.more}
						aria-disabled={limited}
						aria-label={moreLabel}
						aria-haspopup="dialog"
						data-flx="voice-scene.voice-scene-emote-bar.more-button"
					>
						<PlusIcon
							weight="bold"
							className={styles.moreIcon}
							aria-hidden
							data-flx="voice-scene.voice-scene-emote-bar.plus-icon"
						/>
					</button>
				</FocusRing>
			</Popout>
		</div>
	);
});
